import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  App,
  Avatar,
  Button,
  Card,
  Empty,
  Form,
  Input,
  List,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useActiveOrg } from '@/features/organizations/active-org-context';
import { projectsApi } from '../projects.api';

const schema = z.object({
  name: z.string().trim().min(2, 'At least 2 characters').max(80),
  businessModel: z.enum(['marketing', 'faceless_video', 'did_you_know', 'movie_review']),
});
type FormValues = z.infer<typeof schema>;

export function ProjectsListPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeOrg, activeOrgId } = useActiveOrg();
  const isOwner = activeOrg?.role === 'owner';

  const projectsKey = ['projects', activeOrgId] as const;
  const query = useQuery({
    queryKey: projectsKey,
    queryFn: () => projectsApi.list(activeOrgId ?? ''),
    enabled: Boolean(activeOrgId),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', businessModel: 'marketing' },
  });
  const createMutation = useMutation({
    mutationFn: (values: FormValues) => projectsApi.create(activeOrgId ?? '', values),
    onSuccess: async (project) => {
      await queryClient.invalidateQueries({ queryKey: projectsKey });
      form.reset();
      message.success(`Created ${project.name}`);
      void navigate({ to: '/projects/$projectId', params: { projectId: project.id } });
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to create project'),
  });

  if (!activeOrg) return <Typography.Paragraph>Select an organization first.</Typography.Paragraph>;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ marginBottom: 0 }}>
        {activeOrg.name} · Projects
      </Typography.Title>

      {isOwner ? (
        <Card title="New project">
          <Form
            layout="inline"
            onFinish={form.handleSubmit((v) => createMutation.mutate(v))}
            style={{ rowGap: 12 }}
          >
            <Controller
              control={form.control}
              name="name"
              render={({ field, fieldState }) => (
                <Form.Item
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Input {...field} placeholder="Project name" style={{ width: 280 }} />
                </Form.Item>
              )}
            />
            <Controller
              control={form.control}
              name="businessModel"
              render={({ field }) => (
                <Form.Item>
                  <Select
                    {...field}
                    style={{ width: 240 }}
                    options={[
                      { value: 'marketing', label: 'Marketing & campaigns' },
                      { value: 'faceless_video', label: 'Faceless video' },
                      { value: 'did_you_know', label: 'Did you know' },
                      { value: 'movie_review', label: 'Movie reviews' },
                    ]}
                  />
                </Form.Item>
              )}
            />
            <Button type="primary" htmlType="submit" loading={createMutation.isPending}>
              Create project
            </Button>
          </Form>
        </Card>
      ) : null}

      {query.isLoading ? (
        <Spin />
      ) : (query.data ?? []).length === 0 ? (
        <Empty description="No projects yet" />
      ) : (
        <List
          grid={{ gutter: 20, xs: 1, sm: 2, md: 3 }}
          dataSource={query.data ?? []}
          renderItem={(project) => {
            const { primary, secondary, accent } = project.brand.colors;
            const initials = project.name.trim().slice(0, 2).toUpperCase();
            return (
              <List.Item>
                <Card
                  hoverable
                  onClick={() =>
                    navigate({ to: '/projects/$projectId', params: { projectId: project.id } })
                  }
                  styles={{ body: { padding: 0 } }}
                  style={{ overflow: 'hidden', borderRadius: 14 }}
                >
                  {/* Brand-colored banner so each project is visually distinct. */}
                  <div
                    style={{
                      height: 76,
                      background: `linear-gradient(135deg, ${primary} 0%, ${accent} 100%)`,
                    }}
                  />
                  <div style={{ padding: '0 20px 18px' }}>
                    <div style={{ marginTop: -30, marginBottom: 12 }}>
                      <Avatar
                        src={project.brand.logoUrl ?? undefined}
                        size={60}
                        style={{
                          background: primary,
                          border: '3px solid #fff',
                          boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
                          fontSize: 22,
                          fontWeight: 600,
                          verticalAlign: 'middle',
                        }}
                      >
                        {initials}
                      </Avatar>
                    </div>
                    <Typography.Title level={5} style={{ margin: 0 }} ellipsis>
                      {project.name}
                    </Typography.Title>
                    <Typography.Text
                      type="secondary"
                      style={{ fontFamily: 'monospace', fontSize: 12 }}
                    >
                      {project.slug}
                    </Typography.Text>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        marginTop: 16,
                      }}
                    >
                      {[primary, secondary, accent].map((c) => (
                        <span
                          key={c}
                          title={c}
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: 6,
                            background: c,
                            border: '1px solid rgba(0,0,0,0.08)',
                          }}
                        />
                      ))}
                      <Typography.Text
                        type="secondary"
                        style={{ marginLeft: 'auto', fontSize: 12 }}
                      >
                        {new Date(project.createdAt).toLocaleDateString()}
                      </Typography.Text>
                    </div>
                  </div>
                </Card>
              </List.Item>
            );
          }}
        />
      )}
    </Space>
  );
}
